import { Component, Input, OnInit } from '@angular/core';
import { AccountService, ILicenceDetails, LicenceInfoService } from '@nx-fe/services';
import { XsI18nPipe } from '@nx-fe/pipes';
import { DatePipe } from '@angular/common';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export class BarIndicatorSettings {
  /** What exactly this indicator is showing */
  public metric: string;

  /** Show metric value text above the progress bar */
  public metricInfoText: string;

  /** Whether to show metric value text or max & current value text */
  public showMetricInfoText: boolean;

  /** Current value of the indicator */
  public currentValue: number;

  /** Current formatted value of the indicator */
  public currentValueText: string;

  /** Whever to show currentValue instead of maxValue right of metric */
  public currentValueMetric: boolean;

  /** Current value of the indicator in percents */
  public percentValue: any;

  /** Max value of the indicator */
  public maxValue: number;

  /** Max formatted value of the indicator */
  public maxValueText: string;

  /** Available value of the indicator */
  public availableValue: number;

  /** Available formatted value of the indicator */
  public availableValueText: string;

  /** 'success', 'warning' or 'danger' */
  public type = 'success';

  /** 'SUCCESS:WARNING:DANGER' ranges in percents, each in min-max format */
  public ranges: string;

  /** Whever to show the stats block or not */
  public showStats: any;

  /** Whether to show total or available value on right side */
  public showTotal: boolean;

  /** Show warning color if availableValue is less or equal to 0 */
  public showWarning: boolean;
}

@Component({
  selector: 'xs-license-info',
  templateUrl: './license-info.component.html',
  styleUrls: ['./license-info.component.scss'],
})
export class LicenseInfoComponent implements OnInit {
  @Input() licenseUpdated$: Observable<ILicenceDetails>;
  public licenceInfo: ILicenceDetails;
  public licenceDaysUsed: any;
  public licenceDaysRemaining: string;
  public licenceTotalDays: string;
  public licencePeriod: any;
  public licenceExpired: boolean;
  public licenceTypeCaption: any;
  public expireSoon: boolean;
  private warningPeriod = 7;
  private destroy$ = new Subject<void>();
  public barIndicator: BarIndicatorSettings = new BarIndicatorSettings();
  constructor(
    private licenceInfoService: LicenceInfoService,
    private xsI18npipe: XsI18nPipe,
    private datePipe: DatePipe,
    private accountService: AccountService
  ) {
    this.updateLicenseData();
  }

  ngOnInit() {
    this.licenseUpdated$.pipe(takeUntil(this.destroy$)).subscribe((details: ILicenceDetails) => {
      this.updateLicenseData(details);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Initialize or update licence info bar data */
  private updateLicenseData = (details?: ILicenceDetails): void => {
    if (details) {
      this.calculateAndUpdateLicenseDays(details);
      this.setLicenseDetails(details);
      this.accountService.updateLicenceDetails(details);
      this.licenceInfoService.invalidateLicenceInformation();
    } else {
      this.licenceInfoService.getLicenceInformation().subscribe(
        details => {
          if (!!details && details.$daysUsed !== undefined) {
            this.setLicenseDetails(details);
          }
        },
        error => {
          console.log(error);
        }
      );
    }
  };

  private calculateAndUpdateLicenseDays = (licenceDetails: ILicenceDetails): void => {
    let currentDate = new Date();
    const startDate = new Date(licenceDetails.licenseStartDate);
    const endDate = new Date(licenceDetails.licenseEndDate);

    // if current date is greater than the expiration date, current date is equal to the expiration date
    if (currentDate > endDate) {
      currentDate = endDate;
    }

    // if current date is earlier than the activation date, use activation date
    if (currentDate < startDate) {
      currentDate = startDate;
    }

    const totalLengthInDays = this.millisecondsToDays(endDate.getTime() - startDate.getTime());

    const daysUsed = this.millisecondsToDays(currentDate.getTime() - startDate.getTime());

    let daysRemaining = this.millisecondsToDays(endDate.getTime() - currentDate.getTime());

    if (daysRemaining < 0) {
      daysRemaining = 0;
    }

    licenceDetails.$totalLengthInDays = Math.floor(totalLengthInDays);
    licenceDetails.$daysUsed = Math.floor(daysUsed);
    licenceDetails.$daysRemaining = Math.ceil(daysRemaining);
  };

  private millisecondsToDays = (milliseconds: number): number => {
    return milliseconds / 1000 / 60 / 60 / 24;
  };

  /** Set licence info bar data */
  private setLicenseDetails = (details: ILicenceDetails): void => {
    this.licenceInfo = details;
    this.setLicenceDaysUsedString();
    this.setLicenceDaysRemainingString();
    this.setLicenceTotalDaysString();
    this.setLicenceTypeCaption();
    this.setLicencePeriodString();
    this.setLicenceExpired();
    this.initBarIndicator();
  };

  private initBarIndicator() {
    const licenceInfo = this.licenceInfo;
    this.barIndicator.metric = '';
    this.barIndicator.maxValue = licenceInfo.$totalLengthInDays;
    this.barIndicator.maxValueText = this.licenceTotalDays;
    this.barIndicator.currentValue = licenceInfo.$daysUsed;
    this.barIndicator.currentValueText = this.licenceDaysUsed;
    this.barIndicator.availableValue = licenceInfo.$daysRemaining;
    this.barIndicator.availableValueText = this.licenceDaysRemaining;
    this.barIndicator.showStats = true;
    this.barIndicator.metricInfoText = this.licencePeriod;
    this.barIndicator.showMetricInfoText = true;
    this.barIndicator.showWarning = true;
    this.barIndicator.showTotal = true;
    this.barIndicator.percentValue = (licenceInfo.$daysUsed / licenceInfo.$totalLengthInDays) * 100;
  }

  /** Returns trial expiration date as a string */
  private getLicenceExpirationDateString = (): string => {
    return this.datePipe.transform(this.licenceInfo.licenseEndDate, `${this.xsI18npipe.transform('timeFormatFilter_MonthDayYear_Format')}`);
  };

  /**
   * Sets the trial used days as a string
   */
  private setLicenceDaysUsedString = () => {
    this.licenceDaysUsed = `${this.xsI18npipe.transform(
      'licenceInfoBar_LicenceDays_Caption'
    )} ${this.xsI18npipe.transform(this.licenceInfo.$daysUsed.toString())}`;
  };

  /**
   * Sets the trial remaining days as a string
   */
  private setLicenceDaysRemainingString = () => {
    this.licenceDaysRemaining = `${this.xsI18npipe.transform(
      'licenceInfoBar_LicenceDays_Caption'
    )} ${this.xsI18npipe.transform(this.licenceInfo.$daysRemaining.toString())}`;
  };

  /**
   * Returns activation date as a string
   *
   * @returns string The activation date string
   */

  private getLicenceActivationDateString = (): string => {
    return this.datePipe.transform(this.licenceInfo.licenseStartDate, `${this.xsI18npipe.transform('timeFormatFilter_MonthDayYear_Format')}`);
  };
  /**
   * Sets the total days as a string
   */
  private setLicenceTotalDaysString = () => {
    this.licenceTotalDays = `${this.xsI18npipe.transform(
      'licenceInfoBar_LicenceDays_Caption'
    )} ${this.licenceInfo.$totalLengthInDays.toString()}`;
  };

  /**
   * Sets the period from activation date to expiration date
   */
  private setLicencePeriodString = () => {
    this.licencePeriod = `${this.xsI18npipe.transform(
      'licenceInfoBar_Period_Caption',
      this.getLicenceActivationDateString(),
      this.getLicenceExpirationDateString()
    )}`;
  };

  /**
   * Sets the flag that determines if the license is expired or not
   */
  private setLicenceExpired = () => {
    if (this.licenceInfo.$daysRemaining <= 0) {
      this.licenceExpired = true;
    } else {
      this.licenceExpired = false;
    }
  };

  /**
   * Sets the caption describing the licence type
   *
   * @param promise The promise of the get licence info request
   */
  private setLicenceTypeCaption = () => {
    this.expireSoon = this.licenceInfo.$daysRemaining <= this.warningPeriod && this.licenceInfo.$daysRemaining > 0;
    if (this.licenceInfo.licenseType === 'Trial') {
      this.licenceTypeCaption = this.xsI18npipe.transform(
        'licenceInfoBar_TrialPeriod_Caption',
        this.licenceInfo.$daysRemaining.toString()
      );
    } else {
      this.licenceTypeCaption = this.xsI18npipe.transform(
        'licenceInfoBar_CommercialPeriod_Caption',
        this.licenceInfo.$daysRemaining.toString()
      );
    }
  };
}
